import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import api from '../services/api';

/**
 * /sso page — Receives an SSO handoff token from the Sales Dashboard,
 * exchanges it with the QB backend for a QB JWT, stores it,
 * and redirects to the requested page.
 *
 * Expected URL: /sso?token=<jwt>&redirect=/quotations/new
 */
const SsoLogin = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    const ssoToken = params.get('token');
    const redirect = params.get('redirect') || '/';

    if (!ssoToken) {
      setError('Missing SSO token.');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await api.post('/auth/sso-exchange', { ssoToken });
        const { token } = res.data;
        if (cancelled) return;
        localStorage.setItem('token', token);
        // Force the app to re-read auth context by reloading at the target URL.
        window.location.replace(redirect);
      } catch (err) {
        if (cancelled) return;
        setError(
          err.response?.data?.message || 'SSO sign-in failed. Please try again.'
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [params, navigate]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      sx={{ gap: 2 }}
    >
      {error ? (
        <>
          <Alert severity="error" sx={{ maxWidth: 480 }}>
            {error}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            <a href="/login">Return to login</a>
          </Typography>
        </>
      ) : (
        <>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Signing you in…
          </Typography>
        </>
      )}
    </Box>
  );
};

export default SsoLogin;
